package com.bikompanion.ui.composables

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.ScrollState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.CompositingStrategy
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * Fades the content out at whichever edge of a vertical scroll viewport still has more to scroll
 * to, so a clipped sheet reads as "keep going" rather than "cut off".
 *
 * Has to sit *before* the scroll modifier in the chain: it fades the viewport, not the padded
 * content inside it. The fade erases pixels (`DstIn`) instead of painting a colour over them, since
 * the sheet behind is translucent glass and there is no solid colour to fade towards.
 */
@Composable
internal fun Modifier.scrollEdgeFade(
    state: ScrollState,
    length: Dp = 24.dp,
): Modifier {
    val topAlpha by animateFloatAsState(if (state.canScrollBackward) 1f else 0f)
    val bottomAlpha by animateFloatAsState(if (state.canScrollForward) 1f else 0f)
    return this
        .graphicsLayer { compositingStrategy = CompositingStrategy.Offscreen }
        .drawWithContent {
            drawContent()
            val fadePx = length.toPx().coerceAtMost(size.height / 2f)
            if (topAlpha > 0f) {
                drawRect(
                    brush = Brush.verticalGradient(
                        colors = listOf(Color.Black.copy(alpha = 1f - topAlpha), Color.Black),
                        startY = 0f,
                        endY = fadePx,
                    ),
                    size = Size(size.width, fadePx),
                    blendMode = BlendMode.DstIn,
                )
            }
            if (bottomAlpha > 0f) {
                drawRect(
                    brush = Brush.verticalGradient(
                        colors = listOf(Color.Black, Color.Black.copy(alpha = 1f - bottomAlpha)),
                        startY = size.height - fadePx,
                        endY = size.height,
                    ),
                    topLeft = Offset(0f, size.height - fadePx),
                    size = Size(size.width, fadePx),
                    blendMode = BlendMode.DstIn,
                )
            }
        }
}
